import { format, formatDistanceToNowStrict } from 'date-fns';
import Link from 'next/link';

import { tokenFormatter, usdFormatter } from '@/lib/formatting';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';
import { ApproximateAmountTooltip } from '@/components/TradeHistoryTable/TableCell/TokenAmountCell';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ArrowRightIcon } from '@/ui/Icons/ArrowRightIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { ChainAddress } from '@/utils/models';
import { isUsdStableAddress } from '@/utils/multichain';
import { isSameTokenAddress } from '@/utils/normalizeAddress';

export const ROW_CLASSES = classNames(
  'group col-span-full grid grid-cols-subgrid items-center px-2 h-12 text-left',
  'border-b-1 border-token-gray-300 last:border-b-0',
);

/**
 * Sell | Buy | Date | Type, with Network leading and Price inserted from
 * `tablet:` up (their cells are hidden below it, so they drop out of the track
 * flow). Open orders append a cancel-button track. Price has a 72px minimum
 * matching its skeleton; other tracks size to their widest cell. The grid's
 * `justify-between` then spreads the leftover width evenly across the gaps.
 */
export const historyGridCols = (showActions: boolean) =>
  showActions
    ? classNames(
        'grid-cols-[repeat(5,max-content)]',
        'tablet:grid-cols-[repeat(3,max-content)_minmax(72px,max-content)_repeat(3,max-content)]',
      )
    : classNames(
        'grid-cols-[repeat(4,max-content)]',
        'tablet:grid-cols-[repeat(3,max-content)_minmax(72px,max-content)_repeat(2,max-content)]',
      );

export const HistoryHeaderRow = ({
  dateLabel,
  showActions = false,
}: {
  dateLabel: string;
  showActions?: boolean;
}) => (
  <div
    className={classNames(
      'col-span-full grid grid-cols-subgrid items-center px-2 h-10',
      'border-y-1 border-token-gray-300',
      fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-800' }),
    )}
  >
    <span className="hidden tablet:block text-center">Network</span>
    <span>Sell</span>
    <span>Buy</span>
    <span className="hidden tablet:block">Price</span>
    <span>{dateLabel}</span>
    <span>Type</span>
    {showActions && <span className="sr-only">Actions</span>}
  </div>
);

/**
 * The trade module on this page owns the pair, so a logo moves that token into
 * the buy field. The token already on the sell side flips the pair instead of
 * landing on both sides, and the token already being bought is not actionable.
 */
export type BuyTokenSelection = {
  /** The pair's current buy token, whose logo stays inert. */
  current: { address: string; chainId: number | undefined } | undefined;
  onSelect: (token: Pick<TokenBase, 'address' | 'chainId'>) => void;
};

/** One trade leg. The chain badge is suppressed because Network is its own column. */
export const AmountCell = ({
  amount,
  symbol,
  address,
  chainId,
  isApproximate = false,
  buySelection,
}: {
  amount: number;
  symbol: string;
  address: ChainAddress;
  chainId: number | undefined;
  isApproximate?: boolean;
  buySelection?: BuyTokenSelection;
}) => {
  const isBuyToken =
    !!buySelection?.current &&
    buySelection.current.chainId === chainId &&
    isSameTokenAddress(buySelection.current.address, address);
  const selectBuy =
    buySelection && chainId && !isBuyToken
      ? () => buySelection.onSelect({ address, chainId })
      : undefined;
  const leg = (
    <>
      <TokenAvatar
        address={address}
        chainId={chainId}
        size={16}
        showChainLogo={false}
        className="shrink-0"
      />
      <span className="truncate">
        {isApproximate ? '~ ' : ''}
        {tokenFormatter.format(amount)}
      </span>
      <span className="truncate">{symbol}</span>
    </>
  );

  return (
    <div className={fontStyles({ variant: 'LABEL_SM', class: 'flex items-center gap-1 min-w-0' })}>
      {selectBuy ? (
        <MatchaTooltip>
          <MatchaTooltipTrigger>
            <button
              aria-label={`Buy ${symbol}`}
              className={classNames(
                'inline-flex items-center gap-1 min-w-0 h-8 px-2 -mx-2 rounded-sm',
                'hover:bg-token-gray-200 active:bg-token-gray-300',
                'focus-visible:outline-token-gray-500 focus-visible:outline-[3px]',
              )}
              onClick={selectBuy}
              type="button"
            >
              {leg}
            </button>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>Buy {symbol}</MatchaTooltipContent>
          <MatchaTooltipPopover sideOffset={7} />
        </MatchaTooltip>
      ) : (
        <span className="inline-flex items-center gap-1 min-w-0">{leg}</span>
      )}
      {isApproximate && <ApproximateAmountTooltip />}
    </div>
  );
};

export const NetworkCell = ({
  originChainId,
  destinationChainId,
}: {
  originChainId: number | undefined;
  destinationChainId?: number;
}) => {
  const originName = (originChainId && NETWORK_NAME_PER_CHAIN_ID[originChainId]) || 'Unknown';
  const isCrossChain = !!destinationChainId && destinationChainId !== originChainId;
  const destinationName =
    (destinationChainId && NETWORK_NAME_PER_CHAIN_ID[destinationChainId]) || 'Unknown';
  const label = isCrossChain ? `${originName} to ${destinationName}` : originName;

  return (
    <span
      aria-label={label}
      className="hidden tablet:flex items-center justify-center gap-1"
      title={label}
    >
      <ChainAvatar chainId={originChainId} size={16} />
      {isCrossChain && (
        <>
          <ArrowRightIcon
            aria-hidden
            className="shrink-0 text-token-gray-700"
            width={12}
            height={12}
          />
          <ChainAvatar chainId={destinationChainId} size={16} />
        </>
      )}
    </span>
  );
};

type PriceLeg = { address: string; symbol: string; amount: number; chainId: number | undefined };

type RowPriceInput = {
  sell: PriceLeg;
  buy: PriceLeg;
  /** Traded volume of a settled row; absent on open orders. */
  volumeUSD?: number | null;
};

/**
 * Mirrors `/history`'s `getLimitDisplayPrice`: when one side is a USD stable the
 * non-stable token is priced directly in USD, otherwise the price is a token
 * ratio quoting one buy token in sell tokens. `usd` restates that same ratio in
 * USD from the row's traded volume, and is only set when `primary` is not
 * already a USD figure.
 */
export const getRowPrice = ({ sell, buy, volumeUSD }: RowPriceInput) => {
  const sellIsStable = isUsdStableAddress(sell.address, sell.chainId);
  const buyIsStable = isUsdStableAddress(buy.address, buy.chainId);

  if (sellIsStable || buyIsStable) {
    const usdPrice = sellIsStable ? sell.amount / buy.amount : buy.amount / sell.amount;
    return { primary: Number.isFinite(usdPrice) ? usdFormatter.format(usdPrice, true) : '-' };
  }

  const rate = sell.amount / buy.amount;
  if (!Number.isFinite(rate)) return { primary: '-' };

  const usdRate = volumeUSD ? volumeUSD / buy.amount : undefined;
  return {
    primary: `${tokenFormatter.format(rate)} ${sell.symbol}`,
    usd:
      usdRate && Number.isFinite(usdRate) ? `(${usdFormatter.format(usdRate, true)})` : undefined,
  };
};

export const PriceCell = (props: RowPriceInput) => {
  const { primary, usd } = getRowPrice(props);

  return (
    <div className="hidden tablet:flex flex-col min-w-0">
      <span className={fontStyles({ variant: 'BODY_SM', class: 'truncate' })}>{primary}</span>
      {usd && (
        <span className={fontStyles({ variant: 'CAPTION', class: 'truncate text-token-gray-700' })}>
          {usd}
        </span>
      )}
    </div>
  );
};

export const RelativeTime = ({
  date,
  prefix,
  className,
}: {
  date: Date;
  prefix?: string;
  className?: string;
}) => (
  <span
    className={fontStyles({
      variant: 'BODY_SM',
      class: ['text-token-gray-700 whitespace-nowrap', className],
    })}
    title={format(date, 'PPP p')}
  >
    {prefix ? `${prefix} ` : null}
    {formatDistanceToNowStrict(date)}
  </span>
);

const titleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

/** Homepage copy: market trades are Swaps, cross-chain trades are Bridges. */
const formatOrderType = (type: string) =>
  titleCase(type.replace(/\bmarket\b/i, 'swap').replace(/\bcross chain\b/i, 'bridge'));

export const TypeCell = ({
  type,
  detailsHref,
  explorerHref,
}: {
  type: string;
  detailsHref?: Parameters<typeof Link>[0]['href'];
  /** Block explorer fallback for trades the details page cannot resolve. */
  explorerHref?: string;
}) => {
  const label = formatOrderType(type);
  const className = fontStyles({ variant: 'LABEL_SM', class: 'whitespace-nowrap' });

  if (explorerHref) {
    return (
      <ExternalLink className={classNames(className, 'hover:underline')} href={explorerHref}>
        {label}
      </ExternalLink>
    );
  }

  if (detailsHref) {
    return (
      <Link className={classNames(className, 'hover:underline')} href={detailsHref}>
        {label}
      </Link>
    );
  }

  return <span className={className}>{label}</span>;
};

/** Mirrors a data row's tracks, including the two that only exist from `tablet:` up. */
export const HistoryRowSkeleton = () => (
  <div className={ROW_CLASSES}>
    <AvatarSkeleton size={16} className="hidden tablet:block justify-self-center" />
    <TextSkeleton width={96} />
    <TextSkeleton width={96} />
    <TextSkeleton width={72} className="hidden tablet:block" />
    <TextSkeleton width={56} />
    <TextSkeleton width={40} />
  </div>
);
